export const Block = {
  AIR: 0,
  BEDROCK: 1,
  STONE: 2,
  DIRT: 3,
  GRASS: 4,
  SAND: 5,
  GRAVEL: 6,
  CLAY: 7,
  SNOW: 8,
  ICE: 9,
  WATER: 10,
  OAK_LOG: 11,
  OAK_LEAVES: 12,
  BIRCH_LOG: 13,
  BIRCH_LEAVES: 14,
  PINE_LOG: 15,
  PINE_LEAVES: 16,
  CHERRY_LOG: 17,
  CHERRY_LEAVES: 18,
  JUNGLE_LOG: 19,
  JUNGLE_LEAVES: 20,
  CACTUS: 21,
  OAK_PLANKS: 22,
  COBBLE: 23,
  GLASS: 24,
  SANDSTONE: 25,
  COAL_ORE: 26,
  IRON_ORE: 27,
  GOLD_ORE: 28,
  DIAMOND_ORE: 29,
  CRAFTING_TABLE: 30,
  FURNACE: 31,
  CHEST: 32,
  BRICKS: 33,
  TALL_GRASS: 34,
  FLOWER_RED: 35,
  FLOWER_YELLOW: 36,
  FLOWER_PINK: 37,
  MUSHROOM: 38,
  PUMPKIN: 39,
  IRON_BLOCK: 40,
  GOLD_BLOCK: 41,
  DIAMOND_BLOCK: 42,
  GLOWSTONE: 43,
  SNOWY_GRASS: 44,
  BIRCH_PLANKS: 45,
} as const;

export type BlockId = (typeof Block)[keyof typeof Block];

export type FaceName = "px" | "nx" | "py" | "ny" | "pz" | "nz";
export type MeshKind = "cube" | "cross" | "none";
export type ToolType = "none" | "pickaxe" | "axe" | "shovel" | "sword";

export interface BlockDef {
  id: BlockId;
  name: string;
  solid: boolean;
  opaque: boolean;
  liquid: boolean;
  replaceable: boolean;
  plant: boolean;
  mesh: MeshKind;
  hardness: number;
  tool: ToolType;
  drops: { item: number; count: number; chance: number }[];
  tile: { py: number; ny: number; side: number; px?: number };
  emit?: number;
  tintGrass?: boolean;
  tintLeaves?: boolean;
}

const D = (
  partial: Omit<BlockDef, "drops"> & { drops?: BlockDef["drops"] },
): BlockDef => ({
  drops: [{ item: partial.id, count: 1, chance: 1 }],
  ...partial,
});

/** Tile indices into the generated atlas (row-major, 16-wide). */
export const Tiles = {
  bedrock: 0,
  stone: 1,
  dirt: 2,
  grassTop: 3,
  grassSide: 4,
  sand: 5,
  gravel: 6,
  clay: 7,
  snow: 8,
  ice: 9,
  water: 10,
  oakLog: 11,
  oakLogTop: 12,
  oakLeaves: 13,
  birchLog: 14,
  birchLogTop: 15,
  birchLeaves: 16,
  pineLog: 17,
  pineLogTop: 18,
  pineLeaves: 19,
  cherryLog: 20,
  cherryLogTop: 21,
  cherryLeaves: 22,
  jungleLog: 23,
  jungleLogTop: 24,
  jungleLeaves: 25,
  cactus: 26,
  cactusTop: 27,
  oakPlanks: 28,
  cobble: 29,
  glass: 30,
  sandstone: 31,
  coalOre: 32,
  ironOre: 33,
  goldOre: 34,
  diamondOre: 35,
  craftTop: 36,
  craftSide: 37,
  furnaceFront: 38,
  furnaceSide: 39,
  chestFront: 40,
  chestSide: 41,
  bricks: 42,
  tallGrass: 43,
  flowerRed: 44,
  flowerYellow: 45,
  flowerPink: 46,
  mushroom: 47,
  pumpkinSide: 48,
  pumpkinTop: 49,
  ironBlock: 50,
  goldBlock: 51,
  diamondBlock: 52,
  glowstone: 53,
  snowySide: 54,
  birchPlanks: 55,
} as const;

export const BLOCKS: Record<number, BlockDef> = {
  [Block.AIR]: D({
    id: Block.AIR,
    name: "Air",
    solid: false,
    opaque: false,
    liquid: false,
    replaceable: true,
    plant: false,
    mesh: "none",
    hardness: 0,
    tool: "none",
    tile: { py: 0, ny: 0, side: 0 },
    drops: [],
  }),
  [Block.BEDROCK]: D({
    id: Block.BEDROCK,
    name: "Bedrock",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: -1,
    tool: "none",
    tile: { py: Tiles.bedrock, ny: Tiles.bedrock, side: Tiles.bedrock },
    drops: [],
  }),
  [Block.STONE]: D({
    id: Block.STONE,
    name: "Stone",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 1.5,
    tool: "pickaxe",
    tile: { py: Tiles.stone, ny: Tiles.stone, side: Tiles.stone },
    drops: [{ item: Block.COBBLE, count: 1, chance: 1 }],
  }),
  [Block.DIRT]: D({
    id: Block.DIRT,
    name: "Dirt",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 0.5,
    tool: "shovel",
    tile: { py: Tiles.dirt, ny: Tiles.dirt, side: Tiles.dirt },
  }),
  [Block.GRASS]: D({
    id: Block.GRASS,
    name: "Grass",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 0.6,
    tool: "shovel",
    tile: { py: Tiles.grassTop, ny: Tiles.dirt, side: Tiles.grassSide },
    tintGrass: true,
    drops: [{ item: Block.DIRT, count: 1, chance: 1 }],
  }),
  [Block.SAND]: D({
    id: Block.SAND,
    name: "Sand",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 0.5,
    tool: "shovel",
    tile: { py: Tiles.sand, ny: Tiles.sand, side: Tiles.sand },
  }),
  [Block.GRAVEL]: D({
    id: Block.GRAVEL,
    name: "Gravel",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 0.6,
    tool: "shovel",
    tile: { py: Tiles.gravel, ny: Tiles.gravel, side: Tiles.gravel },
  }),
  [Block.CLAY]: D({
    id: Block.CLAY,
    name: "Clay",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 0.6,
    tool: "shovel",
    tile: { py: Tiles.clay, ny: Tiles.clay, side: Tiles.clay },
  }),
  [Block.SNOW]: D({
    id: Block.SNOW,
    name: "Snow",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 0.2,
    tool: "shovel",
    tile: { py: Tiles.snow, ny: Tiles.snow, side: Tiles.snow },
  }),
  [Block.ICE]: D({
    id: Block.ICE,
    name: "Ice",
    solid: true,
    opaque: false,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 0.5,
    tool: "pickaxe",
    tile: { py: Tiles.ice, ny: Tiles.ice, side: Tiles.ice },
  }),
  [Block.WATER]: D({
    id: Block.WATER,
    name: "Water",
    solid: false,
    opaque: false,
    liquid: true,
    replaceable: true,
    plant: false,
    mesh: "cube",
    hardness: 0,
    tool: "none",
    tile: { py: Tiles.water, ny: Tiles.water, side: Tiles.water },
    drops: [],
  }),
  [Block.OAK_LOG]: D({
    id: Block.OAK_LOG,
    name: "Oak Log",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 2,
    tool: "axe",
    tile: { py: Tiles.oakLogTop, ny: Tiles.oakLogTop, side: Tiles.oakLog },
  }),
  [Block.OAK_LEAVES]: D({
    id: Block.OAK_LEAVES,
    name: "Oak Leaves",
    solid: true,
    opaque: false,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 0.2,
    tool: "none",
    tile: { py: Tiles.oakLeaves, ny: Tiles.oakLeaves, side: Tiles.oakLeaves },
    tintLeaves: true,
    drops: [
      { item: Block.OAK_LEAVES, count: 1, chance: 0.08 },
      { item: 261, count: 1, chance: 0.06 },
    ],
  }),
  [Block.BIRCH_LOG]: D({
    id: Block.BIRCH_LOG,
    name: "Birch Log",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 2,
    tool: "axe",
    tile: { py: Tiles.birchLogTop, ny: Tiles.birchLogTop, side: Tiles.birchLog },
  }),
  [Block.BIRCH_LEAVES]: D({
    id: Block.BIRCH_LEAVES,
    name: "Birch Leaves",
    solid: true,
    opaque: false,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 0.2,
    tool: "none",
    tile: { py: Tiles.birchLeaves, ny: Tiles.birchLeaves, side: Tiles.birchLeaves },
    drops: [{ item: Block.BIRCH_LEAVES, count: 1, chance: 0.08 }],
  }),
  [Block.PINE_LOG]: D({
    id: Block.PINE_LOG,
    name: "Pine Log",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 2,
    tool: "axe",
    tile: { py: Tiles.pineLogTop, ny: Tiles.pineLogTop, side: Tiles.pineLog },
  }),
  [Block.PINE_LEAVES]: D({
    id: Block.PINE_LEAVES,
    name: "Pine Needles",
    solid: true,
    opaque: false,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 0.2,
    tool: "none",
    tile: { py: Tiles.pineLeaves, ny: Tiles.pineLeaves, side: Tiles.pineLeaves },
    drops: [{ item: Block.PINE_LEAVES, count: 1, chance: 0.08 }],
  }),
  [Block.CHERRY_LOG]: D({
    id: Block.CHERRY_LOG,
    name: "Cherry Log",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 2,
    tool: "axe",
    tile: { py: Tiles.cherryLogTop, ny: Tiles.cherryLogTop, side: Tiles.cherryLog },
  }),
  [Block.CHERRY_LEAVES]: D({
    id: Block.CHERRY_LEAVES,
    name: "Cherry Blossoms",
    solid: true,
    opaque: false,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 0.2,
    tool: "none",
    tile: { py: Tiles.cherryLeaves, ny: Tiles.cherryLeaves, side: Tiles.cherryLeaves },
    drops: [{ item: Block.CHERRY_LEAVES, count: 1, chance: 0.1 }],
  }),
  [Block.JUNGLE_LOG]: D({
    id: Block.JUNGLE_LOG,
    name: "Jungle Log",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 2,
    tool: "axe",
    tile: { py: Tiles.jungleLogTop, ny: Tiles.jungleLogTop, side: Tiles.jungleLog },
  }),
  [Block.JUNGLE_LEAVES]: D({
    id: Block.JUNGLE_LEAVES,
    name: "Jungle Leaves",
    solid: true,
    opaque: false,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 0.2,
    tool: "none",
    tile: { py: Tiles.jungleLeaves, ny: Tiles.jungleLeaves, side: Tiles.jungleLeaves },
    tintLeaves: true,
    drops: [{ item: Block.JUNGLE_LEAVES, count: 1, chance: 0.08 }],
  }),
  [Block.CACTUS]: D({
    id: Block.CACTUS,
    name: "Cactus",
    solid: true,
    opaque: false,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 0.4,
    tool: "none",
    tile: { py: Tiles.cactusTop, ny: Tiles.cactusTop, side: Tiles.cactus },
  }),
  [Block.OAK_PLANKS]: D({
    id: Block.OAK_PLANKS,
    name: "Oak Planks",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 2,
    tool: "axe",
    tile: { py: Tiles.oakPlanks, ny: Tiles.oakPlanks, side: Tiles.oakPlanks },
  }),
  [Block.COBBLE]: D({
    id: Block.COBBLE,
    name: "Cobblestone",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 2,
    tool: "pickaxe",
    tile: { py: Tiles.cobble, ny: Tiles.cobble, side: Tiles.cobble },
  }),
  [Block.GLASS]: D({
    id: Block.GLASS,
    name: "Glass",
    solid: true,
    opaque: false,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 0.3,
    tool: "none",
    tile: { py: Tiles.glass, ny: Tiles.glass, side: Tiles.glass },
    drops: [],
  }),
  [Block.SANDSTONE]: D({
    id: Block.SANDSTONE,
    name: "Sandstone",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 0.8,
    tool: "pickaxe",
    tile: { py: Tiles.sandstone, ny: Tiles.sandstone, side: Tiles.sandstone },
  }),
  [Block.COAL_ORE]: D({
    id: Block.COAL_ORE,
    name: "Coal Ore",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 3,
    tool: "pickaxe",
    tile: { py: Tiles.coalOre, ny: Tiles.coalOre, side: Tiles.coalOre },
    drops: [{ item: 256, count: 1, chance: 1 }],
  }),
  [Block.IRON_ORE]: D({
    id: Block.IRON_ORE,
    name: "Iron Ore",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 3,
    tool: "pickaxe",
    tile: { py: Tiles.ironOre, ny: Tiles.ironOre, side: Tiles.ironOre },
  }),
  [Block.GOLD_ORE]: D({
    id: Block.GOLD_ORE,
    name: "Gold Ore",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 3,
    tool: "pickaxe",
    tile: { py: Tiles.goldOre, ny: Tiles.goldOre, side: Tiles.goldOre },
  }),
  [Block.DIAMOND_ORE]: D({
    id: Block.DIAMOND_ORE,
    name: "Diamond Ore",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 3.5,
    tool: "pickaxe",
    tile: { py: Tiles.diamondOre, ny: Tiles.diamondOre, side: Tiles.diamondOre },
    drops: [{ item: 259, count: 1, chance: 1 }],
  }),
  [Block.CRAFTING_TABLE]: D({
    id: Block.CRAFTING_TABLE,
    name: "Crafting Table",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 2.5,
    tool: "axe",
    tile: { py: Tiles.craftTop, ny: Tiles.oakPlanks, side: Tiles.craftSide },
  }),
  [Block.FURNACE]: D({
    id: Block.FURNACE,
    name: "Furnace",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 3.5,
    tool: "pickaxe",
    tile: {
      py: Tiles.cobble,
      ny: Tiles.cobble,
      side: Tiles.furnaceSide,
      px: Tiles.furnaceFront,
    },
  }),
  [Block.CHEST]: D({
    id: Block.CHEST,
    name: "Chest",
    solid: true,
    opaque: false,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 2.5,
    tool: "axe",
    tile: {
      py: Tiles.chestSide,
      ny: Tiles.chestSide,
      side: Tiles.chestSide,
      px: Tiles.chestFront,
    },
  }),
  [Block.BRICKS]: D({
    id: Block.BRICKS,
    name: "Bricks",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 2,
    tool: "pickaxe",
    tile: { py: Tiles.bricks, ny: Tiles.bricks, side: Tiles.bricks },
  }),
  [Block.TALL_GRASS]: D({
    id: Block.TALL_GRASS,
    name: "Tall Grass",
    solid: false,
    opaque: false,
    liquid: false,
    replaceable: true,
    plant: true,
    mesh: "cross",
    hardness: 0,
    tool: "none",
    tile: { py: Tiles.tallGrass, ny: Tiles.tallGrass, side: Tiles.tallGrass },
    tintGrass: true,
    drops: [{ item: 260, count: 1, chance: 0.18 }],
  }),
  [Block.FLOWER_RED]: D({
    id: Block.FLOWER_RED,
    name: "Poppy",
    solid: false,
    opaque: false,
    liquid: false,
    replaceable: true,
    plant: true,
    mesh: "cross",
    hardness: 0,
    tool: "none",
    tile: { py: Tiles.flowerRed, ny: Tiles.flowerRed, side: Tiles.flowerRed },
  }),
  [Block.FLOWER_YELLOW]: D({
    id: Block.FLOWER_YELLOW,
    name: "Dandelion",
    solid: false,
    opaque: false,
    liquid: false,
    replaceable: true,
    plant: true,
    mesh: "cross",
    hardness: 0,
    tool: "none",
    tile: { py: Tiles.flowerYellow, ny: Tiles.flowerYellow, side: Tiles.flowerYellow },
  }),
  [Block.FLOWER_PINK]: D({
    id: Block.FLOWER_PINK,
    name: "Blossom",
    solid: false,
    opaque: false,
    liquid: false,
    replaceable: true,
    plant: true,
    mesh: "cross",
    hardness: 0,
    tool: "none",
    tile: { py: Tiles.flowerPink, ny: Tiles.flowerPink, side: Tiles.flowerPink },
  }),
  [Block.MUSHROOM]: D({
    id: Block.MUSHROOM,
    name: "Mushroom",
    solid: false,
    opaque: false,
    liquid: false,
    replaceable: true,
    plant: true,
    mesh: "cross",
    hardness: 0,
    tool: "none",
    tile: { py: Tiles.mushroom, ny: Tiles.mushroom, side: Tiles.mushroom },
  }),
  [Block.PUMPKIN]: D({
    id: Block.PUMPKIN,
    name: "Pumpkin",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 1,
    tool: "axe",
    tile: { py: Tiles.pumpkinTop, ny: Tiles.pumpkinTop, side: Tiles.pumpkinSide },
  }),
  [Block.IRON_BLOCK]: D({
    id: Block.IRON_BLOCK,
    name: "Iron Block",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 5,
    tool: "pickaxe",
    tile: { py: Tiles.ironBlock, ny: Tiles.ironBlock, side: Tiles.ironBlock },
  }),
  [Block.GOLD_BLOCK]: D({
    id: Block.GOLD_BLOCK,
    name: "Gold Block",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 3,
    tool: "pickaxe",
    tile: { py: Tiles.goldBlock, ny: Tiles.goldBlock, side: Tiles.goldBlock },
  }),
  [Block.DIAMOND_BLOCK]: D({
    id: Block.DIAMOND_BLOCK,
    name: "Diamond Block",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 5,
    tool: "pickaxe",
    tile: { py: Tiles.diamondBlock, ny: Tiles.diamondBlock, side: Tiles.diamondBlock },
  }),
  [Block.GLOWSTONE]: D({
    id: Block.GLOWSTONE,
    name: "Glowstone",
    solid: true,
    opaque: false,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 0.3,
    tool: "none",
    tile: { py: Tiles.glowstone, ny: Tiles.glowstone, side: Tiles.glowstone },
    emit: 1,
  }),
  [Block.SNOWY_GRASS]: D({
    id: Block.SNOWY_GRASS,
    name: "Snowy Grass",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 0.6,
    tool: "shovel",
    tile: { py: Tiles.snow, ny: Tiles.dirt, side: Tiles.snowySide },
    drops: [{ item: Block.DIRT, count: 1, chance: 1 }],
  }),
  [Block.BIRCH_PLANKS]: D({
    id: Block.BIRCH_PLANKS,
    name: "Birch Planks",
    solid: true,
    opaque: true,
    liquid: false,
    replaceable: false,
    plant: false,
    mesh: "cube",
    hardness: 2,
    tool: "axe",
    tile: { py: Tiles.birchPlanks, ny: Tiles.birchPlanks, side: Tiles.birchPlanks },
  }),
};

export function blockDef(id: number): BlockDef {
  return BLOCKS[id] ?? BLOCKS[Block.AIR]!;
}

export function isSolid(id: number): boolean {
  return blockDef(id).solid;
}

export function isOpaque(id: number): boolean {
  return blockDef(id).opaque;
}

export function isLiquid(id: number): boolean {
  return blockDef(id).liquid;
}

export function isReplaceable(id: number): boolean {
  return blockDef(id).replaceable;
}

export function isPlant(id: number): boolean {
  return blockDef(id).plant;
}

export function isLeaf(id: number): boolean {
  return (
    id === Block.OAK_LEAVES ||
    id === Block.BIRCH_LEAVES ||
    id === Block.PINE_LEAVES ||
    id === Block.CHERRY_LEAVES ||
    id === Block.JUNGLE_LEAVES
  );
}

export function isWater(id: number): boolean {
  return id === Block.WATER;
}

export function isLog(id: number): boolean {
  return (
    id === Block.OAK_LOG ||
    id === Block.BIRCH_LOG ||
    id === Block.PINE_LOG ||
    id === Block.CHERRY_LOG ||
    id === Block.JUNGLE_LOG
  );
}

export function tileForFace(id: number, face: FaceName): number {
  const t = blockDef(id).tile;
  if (face === "py") return t.py;
  if (face === "ny") return t.ny;
  if (face === "px" && t.px !== undefined) return t.px;
  return t.side;
}

export function faceOccludes(id: number): boolean {
  const d = blockDef(id);
  return d.opaque && d.mesh === "cube";
}

export const FACE_DIRS: { name: FaceName; dx: number; dy: number; dz: number }[] = [
  { name: "px", dx: 1, dy: 0, dz: 0 },
  { name: "nx", dx: -1, dy: 0, dz: 0 },
  { name: "py", dx: 0, dy: 1, dz: 0 },
  { name: "ny", dx: 0, dy: -1, dz: 0 },
  { name: "pz", dx: 0, dy: 0, dz: 1 },
  { name: "nz", dx: 0, dy: 0, dz: -1 },
];
